/**
 * I18n Provider - Provides timezone and language settings to the application
 * 
 * Features:
 * - Fetches user preferences from backend when authenticated
 * - Falls back to localStorage/browser detection for unauthenticated users
 * - Syncs preferences to backend on changes
 * - Provides helper functions for formatting dates in user's timezone
 */

import { type ReactNode, useCallback, useMemo, useEffect, useState } from "react";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { useUserPreferencesWithMutation } from "@/backend/hooks/use-user-preferences";
import {
  getBrowserTimezone,
  formatDateInTimezone,
  formatTradingTime as formatTradingTimeUtil,
  formatTradeDate as formatTradeDateUtil,
  isValidTimezone,
  type SupportedLocale,
} from "@nordex/i18n";
import {
  I18nContext,
  DEFAULT_TIMEZONE,
  DEFAULT_LANGUAGE,
  DEFAULT_CLOCK_FORMAT,
  type I18nContextValue,
  type ClockFormat,
} from "./i18n-context";

// localStorage keys for persisting preferences locally
const LOCAL_STORAGE_TIMEZONE_KEY = "nordex_user_timezone";
const LOCAL_STORAGE_LANGUAGE_KEY = "nordex_user_language";
const LOCAL_STORAGE_CLOCK_FORMAT_KEY = "nordex_clock_format";

interface I18nProviderProps {
  children: ReactNode;
}

/**
 * Get timezone from localStorage or browser detection
 */
function getLocalTimezone(): string {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_TIMEZONE_KEY);
    if (stored && isValidTimezone(stored)) {
      return stored;
    }
    // Fall back to browser timezone if valid, otherwise UTC
    const browserTz = getBrowserTimezone();
    return isValidTimezone(browserTz) ? browserTz : DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

/**
 * Get language from localStorage or default
 */
function getLocalLanguage(): SupportedLocale {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_LANGUAGE_KEY);
    if (stored === "en-US" || stored === "ar-AE") {
      return stored;
    }
    return DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

/**
 * Save timezone to localStorage
 */
function saveLocalTimezone(timezone: string): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_TIMEZONE_KEY, timezone);
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Save language to localStorage
 */
function saveLocalLanguage(language: SupportedLocale): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_LANGUAGE_KEY, language);
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Get clock format from localStorage or default
 */
function getLocalClockFormat(): ClockFormat {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_CLOCK_FORMAT_KEY);
    if (stored === "12h" || stored === "24h") {
      return stored;
    }
    return DEFAULT_CLOCK_FORMAT;
  } catch {
    return DEFAULT_CLOCK_FORMAT;
  }
}

/**
 * Save clock format to localStorage
 */
function saveLocalClockFormat(format: ClockFormat): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_CLOCK_FORMAT_KEY, format);
  } catch {
    // Ignore localStorage errors
  }
}

export const I18nProvider = ({ children }: I18nProviderProps) => {
  const { userId, isAuthenticated } = useUnifiedAuth();
  
  // Fetch preferences from backend when authenticated
  const {
    preferences,
    isLoading: isLoadingPreferences,
    updatePreferences,
    isUpdating,
  } = useUserPreferencesWithMutation(userId ?? undefined);

  // Local state for timezone, language, and clock format (used when unauthenticated or loading)
  const [localTimezone, setLocalTimezone] = useState<string>(getLocalTimezone);
  const [localLanguage, setLocalLanguage] = useState<SupportedLocale>(getLocalLanguage);
  const [localClockFormat, setLocalClockFormat] = useState<ClockFormat>(getLocalClockFormat);

  // Determine actual timezone to use
  const timezone = useMemo(() => {
    // If authenticated and have backend preferences, use those
    if (isAuthenticated && preferences?.timezone) {
      return preferences.timezone;
    }
    // Otherwise use local state
    return localTimezone;
  }, [isAuthenticated, preferences?.timezone, localTimezone]);

  // Determine actual language to use
  const language = useMemo((): SupportedLocale => {
    // If authenticated and have backend preferences, use those
    if (isAuthenticated && preferences?.language) {
      // Validate it's a supported locale
      if (preferences.language === "en-US" || preferences.language === "ar-AE") {
        return preferences.language;
      }
    }
    // Otherwise use local state
    return localLanguage;
  }, [isAuthenticated, preferences?.language, localLanguage]);

  // Determine actual clock format to use
  const clockFormat = useMemo((): ClockFormat => {
    // If authenticated and have backend preferences, use those
    if (isAuthenticated && preferences?.clock_format) {
      // Validate it's a supported format
      if (preferences.clock_format === "12h" || preferences.clock_format === "24h") {
        return preferences.clock_format;
      }
    }
    // Otherwise use local state
    return localClockFormat;
  }, [isAuthenticated, preferences?.clock_format, localClockFormat]);

  // Sync backend preferences to local state when they load
  useEffect(() => {
    if (isAuthenticated && preferences) {
      if (preferences.timezone && isValidTimezone(preferences.timezone)) {
        setLocalTimezone(preferences.timezone);
        saveLocalTimezone(preferences.timezone);
      }
      if (preferences.language === "en-US" || preferences.language === "ar-AE") {
        setLocalLanguage(preferences.language);
        saveLocalLanguage(preferences.language);
      }
      if (preferences.clock_format === "12h" || preferences.clock_format === "24h") {
        setLocalClockFormat(preferences.clock_format);
        saveLocalClockFormat(preferences.clock_format);
      }
    }
  }, [isAuthenticated, preferences]);

  // Update timezone handler
  const updateTimezone = useCallback(async (newTimezone: string): Promise<void> => {
    if (!isValidTimezone(newTimezone)) {
      console.warn(`[I18nProvider] Invalid timezone: ${newTimezone}`);
      return;
    }

    // Always update local state and localStorage
    setLocalTimezone(newTimezone);
    saveLocalTimezone(newTimezone);

    // If authenticated, also update backend
    if (isAuthenticated && userId) {
      try {
        await updatePreferences({ timezone: newTimezone });
      } catch (error) {
        console.error("[I18nProvider] Failed to update timezone on backend:", error);
        // Don't throw - local state is already updated
      }
    }
  }, [isAuthenticated, userId, updatePreferences]);

  // Update language handler
  const updateLanguage = useCallback(async (newLanguage: SupportedLocale): Promise<void> => {
    // Validate
    if (newLanguage !== "en-US" && newLanguage !== "ar-AE") {
      console.warn(`[I18nProvider] Invalid language: ${newLanguage}`);
      return;
    }

    // Always update local state and localStorage
    setLocalLanguage(newLanguage);
    saveLocalLanguage(newLanguage);

    // If authenticated, also update backend
    if (isAuthenticated && userId) {
      try {
        await updatePreferences({ language: newLanguage });
      } catch (error) {
        console.error("[I18nProvider] Failed to update language on backend:", error);
        // Don't throw - local state is already updated
      }
    }
  }, [isAuthenticated, userId, updatePreferences]);

  // Update clock format handler
  const updateClockFormat = useCallback(async (newFormat: ClockFormat): Promise<void> => {
    // Validate
    if (newFormat !== "12h" && newFormat !== "24h") {
      console.warn(`[I18nProvider] Invalid clock format: ${newFormat}`);
      return;
    }

    // Always update local state and localStorage
    setLocalClockFormat(newFormat);
    saveLocalClockFormat(newFormat);

    // If authenticated, also update backend
    if (isAuthenticated && userId) {
      try {
        await updatePreferences({ clock_format: newFormat });
      } catch (error) {
        console.error("[I18nProvider] Failed to update clock format on backend:", error);
        // Don't throw - local state is already updated
      }
    }
  }, [isAuthenticated, userId, updatePreferences]);

  // Helper functions that use the user's timezone and clock format
  const formatDate = useCallback((date: Date | string | number, format?: string): string => {
    return formatDateInTimezone(date, timezone, format, language);
  }, [timezone, language]);

  const formatTradingTime = useCallback((date: Date | string | number): string => {
    return formatTradingTimeUtil(date, timezone, language, clockFormat);
  }, [timezone, language, clockFormat]);

  const formatTradeDate = useCallback((date: Date | string | number): string => {
    return formatTradeDateUtil(date, timezone, language, clockFormat);
  }, [timezone, language, clockFormat]);

  // Context value
  const value: I18nContextValue = useMemo(() => ({
    timezone,
    language,
    clockFormat,
    isLoading: isLoadingPreferences || isUpdating,
    updateTimezone,
    updateLanguage,
    updateClockFormat,
    formatDate,
    formatTradingTime,
    formatTradeDate,
  }), [
    timezone,
    language,
    clockFormat,
    isLoadingPreferences,
    isUpdating,
    updateTimezone,
    updateLanguage,
    updateClockFormat,
    formatDate,
    formatTradingTime,
    formatTradeDate,
  ]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};
